import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import usePaginationFromEndpoint, {
    usePaginationFromEndpointProps,
} from "@/components/templates/PaginatedList/usePaginationFromEndpoint";
import useSearchFromEndpoint, {
    useSearchFromEndpointProps,
} from "@/components/templates/SearchList/useSearchFromEndpoint";
import { FieldPath } from "@/core/fieldPath";
import { requestApi } from "@/shared/apis";
import { RequestsQueryParameters, searchableRequestMapper } from "@/shared/apis/requestApi";
import { Request, RequestPagination, RequestSortProperty, SearchableRequest, SortOrder } from "@/shared/domain";
import { usePolicyholders } from "@/shared/hooks/usePolicyholders";

type useRequestQueriesProps = {
    tableMode: boolean;
    useRequestsPageQuery: ({ defaultCategory }) => usePaginationFromEndpointProps<Request, RequestSortProperty>;
    useRequestsSearchQuery: ({ textSearch, sortProperty }) => useSearchFromEndpointProps<Request>;
};

export const requestSearchFilters: FieldPath<SearchableRequest>[] = [
    "requestActionName",
    "remark",
    "userName",
    "documentsQuantity",
    "updateDatetimeFormatted",
    "status.name",
];

const useRequestsQueries = (): useRequestQueriesProps => {
    const { policyholders } = usePolicyholders();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const tableMode = !isSmallScreen;

    const useRequestsPageQuery = ({ defaultCategory }: { defaultCategory: MyBrokerApi.UserRequestStatusFilter }) => {
        return usePaginationFromEndpoint<Request, RequestPagination, RequestsQueryParameters, RequestSortProperty>({
            objectType: "request",
            policyholdersIdList: policyholders,
            endPoint: requestApi.getRequests,
            defaultCategory,
            tableMode,
            defaultSorting: { property: "updateDatetime", order: SortOrder.Descending },
        });
    };

    const useRequestsSearchQuery = ({ textSearch, sortProperty }) => {
        const filterRequests = (results: Request[]) =>
            searchableRequestMapper(results).filterBy(textSearch, requestSearchFilters).sortBy(sortProperty);

        return useSearchFromEndpoint<Request, RequestPagination, RequestsQueryParameters>(
            "requests",
            requestApi.getRequestsLazy,
            policyholders,
            filterRequests,
        );
    };

    return {
        tableMode,
        useRequestsPageQuery,
        useRequestsSearchQuery,
    };
};

export default useRequestsQueries;
